import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@client/components/ui/dialog';
import { Button } from '@design-system';
import { Input } from '@client/components/ui/input';
import { Label } from '@client/components/ui/label';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import {
  AlertCircle,
  Calendar,
  Send,
  Users,
  Mail,
  CheckCircle,
  Loader2,
  Wand2,
} from 'lucide-react';
import { format } from 'date-fns';
import { RadioGroup, RadioGroupItem } from '@client/components/ui/radio-group';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useIsPilotRestricted, usePilotTeamAccess } from '@/shared/hooks/use-active-pilot-program';
import { SeatExpirationCard } from '@client/components/advisor-account-seats/SeatExpirationCard';
import { useInviteSeatPeriod } from './useInviteSeatPeriod';

type Step = 'email' | 'access' | 'seat' | 'review';

const stepLabels: Record<Step, string> = {
  email: 'Advisor Email',
  access: 'Client Access',
  seat: 'Access Period',
  review: 'Review & Confirm',
};

interface InviteAdvisorDialogProps {
  open: boolean;
  onClose: () => void;
  /** A pilot's designated inviter also passes the end date of the seat sent with the invitation. */
  onInvite: (
    email: string,
    accessMode: ClientAccessMode,
    seatExpiresDateTime?: string | null,
  ) => void;
  onDirectAssign?: (email: string, accessMode: ClientAccessMode) => void;
  validateEmail: (email: string) => boolean;
  emailError?: string | null;
  onEmailChange?: (email: string) => void;
  isMutating?: boolean;
  currentMutation?: 'invite' | 'direct' | null;
  isAdminMode?: boolean;
}

const accessModeReviewLabel: Record<ClientAccessMode, string> = {
  full: 'Yes, full access to all of their clients',
  specific: "Specific clients only, they'll choose which clients to share with you after accepting",
  none: "No, you won't have access to their clients",
};

export function InviteAdvisorDialog({
  open,
  onClose,
  onInvite,
  onDirectAssign,
  validateEmail,
  emailError,
  onEmailChange,
  isMutating = false,
  currentMutation = null,
  isAdminMode = false,
}: InviteAdvisorDialogProps) {
  const [step, setStep] = useState<Step>('email');
  const [emailInput, setEmailInput] = useState('');
  const { flags } = useFeatureFlags();
  const shareSpecificClientsEnabled = flags.shareSpecificClients ?? false;
  // Staff setting a team up through admin mode keep every option: pilot onboarding creates full
  // links on purpose, and the server only refuses the pilot advisor's own requests.
  const isClientSharingRestricted = useIsPilotRestricted('client-sharing') && !isAdminMode;
  const defaultAccessMode: ClientAccessMode = isClientSharingRestricted ? 'none' : 'full';
  const [accessMode, setAccessMode] = useState<ClientAccessMode>(defaultAccessMode);
  const effectiveAccessMode: ClientAccessMode = isClientSharingRestricted ? 'none' : accessMode;
  // A pilot's designated inviter adds agents to their team and their subscription in one
  // invitation: client access is always none, and the dialog asks for the seat's end date instead.
  const isPilotInviter = usePilotTeamAccess() === 'owner-only' && !isAdminMode;
  const seatPeriod = useInviteSeatPeriod();
  const { reset: resetSeatPeriod } = seatPeriod;

  useEffect(() => {
    if (open) {
      setStep('email');
      setEmailInput('');
      setAccessMode(defaultAccessMode);
      resetSeatPeriod();
    }
  }, [open, defaultAccessMode, resetSeatPeriod]);

  const handleClose = () => {
    if (isMutating) return;
    setStep('email');
    setEmailInput('');
    setAccessMode(defaultAccessMode);
    resetSeatPeriod();
    onClose();
  };

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    onEmailChange?.(value);
  };

  const handleContinue = () => {
    if (emailInput && !emailError && validateEmail(emailInput)) {
      setStep(isPilotInviter ? 'seat' : 'access');
    }
  };

  const handleSendInvite = () => {
    if (!emailInput || emailError) return;
    if (isPilotInviter) {
      onInvite(emailInput, 'none', seatPeriod.seatExpiresDateTime);
    } else {
      onInvite(emailInput, effectiveAccessMode);
    }
  };

  const handleDirectAssign = () => {
    if (emailInput && !emailError && onDirectAssign) {
      onDirectAssign(emailInput, effectiveAccessMode);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">Invite Advisor</DialogTitle>
          <DialogDescription className="text-gray-700">{stepLabels[step]}</DialogDescription>
        </DialogHeader>

        {/* Step: Email Input */}
        {step === 'email' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Mail className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Enter the advisor's email address
                  </h3>
                  <p className="text-sm text-gray-700">
                    {isPilotInviter
                      ? "We'll send them one invitation to join your team and your subscription"
                      : "We'll send them an invitation to join your team"}
                  </p>
                </div>
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label
                    htmlFor="email"
                    className="text-primary-dark"
                  >
                    Advisor Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="advisor@example.com"
                    value={emailInput}
                    onChange={e => handleEmailInputChange(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleContinue();
                    }}
                  />
                </div>
                {emailError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{emailError}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!emailInput || !!emailError}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step: Client Access */}
        {step === 'access' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Users className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    Do you need access to this advisor's clients?
                  </h3>
                  <p className="text-sm text-gray-700">
                    {isClientSharingRestricted
                      ? 'Client sharing between advisors is not part of the pilot, so this advisor joins your team for reporting and oversight only.'
                      : "Choose how much access you'll have to the clients they bring to the team"}
                  </p>
                </div>
              </div>
              <RadioGroup
                value={effectiveAccessMode}
                onValueChange={value => setAccessMode(value as ClientAccessMode)}
                className="gap-2"
              >
                {!isClientSharingRestricted && (
                  <div className="flex items-start space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                    <RadioGroupItem
                      value="full"
                      id="access-mode-full"
                      className="mt-1"
                    />
                    <Label
                      htmlFor="access-mode-full"
                      className="flex flex-1 cursor-pointer flex-col gap-1"
                    >
                      <span className="text-base font-medium text-gray-900">
                        Yes, full access to all of their clients
                      </span>
                      <span className="text-sm font-normal text-gray-600">
                        View and edit every client they own.
                      </span>
                    </Label>
                  </div>
                )}
                {shareSpecificClientsEnabled && !isClientSharingRestricted && (
                  <div className="flex items-start space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                    <RadioGroupItem
                      value="specific"
                      id="access-mode-specific"
                      className="mt-1"
                    />
                    <Label
                      htmlFor="access-mode-specific"
                      className="flex flex-1 cursor-pointer flex-col gap-1"
                    >
                      <span className="text-base font-medium text-gray-900">
                        Yes, but only specific clients
                      </span>
                      <span className="text-sm font-normal text-gray-600">
                        After they accept, they can choose which of their clients to share with you
                        from their dashboard.
                      </span>
                    </Label>
                  </div>
                )}
                <div className="flex items-start space-x-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300 hover:bg-gray-50">
                  <RadioGroupItem
                    value="none"
                    id="access-mode-none"
                    className="mt-1"
                  />
                  <Label
                    htmlFor="access-mode-none"
                    className="flex flex-1 cursor-pointer flex-col gap-1"
                  >
                    <span className="text-base font-medium text-gray-900">
                      No, I don't need access to their clients
                    </span>
                    <span className="text-sm font-normal text-gray-600">
                      They'll join your team for reporting and oversight only.
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep('email')}
              >
                Back
              </Button>
              <Button onClick={() => setStep('review')}>Continue</Button>
            </DialogFooter>
          </>
        )}

        {/* Step: Access Period (pilot inviter only) */}
        {step === 'seat' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <Calendar className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">
                    How long should their access last?
                  </h3>
                  <p className="text-sm text-gray-700">
                    They get a seat on your subscription when they accept, and join your team for
                    reporting and oversight only. You won't have access to their clients.
                  </p>
                </div>
              </div>
              <SeatExpirationCard
                value={seatPeriod.expirationValue}
                onChange={seatPeriod.setExpirationValue}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStep('email')}
              >
                Back
              </Button>
              <Button
                onClick={() => setStep('review')}
                disabled={!seatPeriod.isValid}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step: Final Review */}
        {step === 'review' && (
          <>
            <div className="py-4">
              <div className="mb-4 flex items-start gap-3">
                <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
                  <CheckCircle className="text-primary-dark h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-xl font-semibold text-gray-900">Review and Confirm</h3>
                  <p className="text-sm text-gray-700">
                    Please verify the details before proceeding
                  </p>
                </div>
              </div>

              <div className="space-y-4 rounded-md border p-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Advisor Email</p>
                  <p className="text-base text-gray-900">{emailInput}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700">Client Access</p>
                  <p className="text-base text-gray-900">
                    {accessModeReviewLabel[isPilotInviter ? 'none' : effectiveAccessMode]}
                  </p>
                </div>

                {isPilotInviter && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Access Ends</p>
                    <p className="text-base text-gray-900">
                      {seatPeriod.seatExpiresDateTime
                        ? format(new Date(seatPeriod.seatExpiresDateTime), 'MMM d, yyyy')
                        : 'No end date'}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-primary-50 border-primary-100 mt-2 rounded-md border p-4">
                <p className="text-primary-dark text-sm">
                  {isPilotInviter &&
                    'When they accept, they join your team and take a seat on your subscription. '}
                  Note: You will have access to view the advisor's subscription and usage statistics
                  by default.
                </p>
              </div>
            </div>

            <DialogFooter>
              <div className="flex w-full justify-between">
                {isAdminMode && onDirectAssign ? (
                  <Button
                    onClick={handleDirectAssign}
                    disabled={isMutating}
                    variant="outline"
                  >
                    {isMutating && currentMutation === 'direct' ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <Wand2 className="mr-1 h-4 w-4" />
                        Assign Directly
                      </>
                    )}
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep(isPilotInviter ? 'seat' : 'access')}
                    disabled={isMutating}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSendInvite}
                    disabled={isMutating}
                  >
                    {isMutating && currentMutation === 'invite' ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-1 h-4 w-4" />
                        Send Invite
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
